import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';  // Import the necessary functions
import { db, app, auth } from '../firebase'; // Assuming you have firebase initialized here

// Initialize Firebase Functions
const functions = getFunctions(app);  // Initialize Firebase Functions with the app



const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const queryTempStripeUser = async (email) => {

    console.log("queryTempStripeUser: " + email);
    
    let retries = 0;
    const maxRetries = 5;
  
    while (retries < maxRetries) {
      // Step 1: Create a query for the 'tempStripeUser' collection where the 'email' field equals the input email
      const tempStripeUserQuery = query(
        collection(db, 'tempStripeUser'),
        where('email', '==', email)
      );
  
      // Step 2: Execute the query and get matching documents
      const querySnapshot = await getDocs(tempStripeUserQuery);
  
      // Step 3: Check if any document exists
      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];  // Get the first matching document
        const data = docSnapshot.data();
  
        return {
          priceId: data.priceId,
          stripeCustomerId: data.stripeCustomerId,
          subscriptionStatus: data.subscriptionStatus,
          subscriptionId: data.subscriptionId,
          subscriptionCreated: data.subscriptionCreated,
          subscriptionProduct: data.subscriptionProduct,
          subscriptionCurrentPeriodEnd: data.subscriptionCurrentPeriodEnd,
        };
      }
  
      retries++;
      if (retries < maxRetries) {
        await wait(2000); // Wait for 2 seconds before retrying
      }
    }
  
    return null; // If no matching document is found after retries
  };




  export const updateUserAccount = async (email, tempStripeUserData) => {
    // Step 1: Query for the user document where the email field matches the provided email
    const userQuery = query(
      collection(db, 'user'),
      where('email', '==', email)
    );
  
    // Step 2: Execute the query and get matching documents
    const querySnapshot = await getDocs(userQuery);
  
    // Step 3: Check if a document with the provided email exists
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0]; // Get the first matching document
      const userRef = userDoc.ref; // Get the reference of the user document
  
      // Step 4: Update the document with the tempStripeUserData
      await updateDoc(userRef, {
        priceId: tempStripeUserData.priceId,
        stripeCustomerId: tempStripeUserData.stripeCustomerId,
        subscriptionStatus: tempStripeUserData.subscriptionStatus,
        subscriptionId: tempStripeUserData.subscriptionId,
        subscriptionCreated: tempStripeUserData.subscriptionCreated,
        subscriptionProduct: tempStripeUserData.subscriptionProduct,
        subscriptionCurrentPeriodEnd: tempStripeUserData.subscriptionCurrentPeriodEnd,
        isDigestPaid: true, // Set isDigestPaid to true
      });
  
      return true; // Return true if successfully updated
    }
  
    return false; // Return false if no document with the provided email was found
  };




  // Creates a new user document in Firestore, including the user's name.
  export const createUserDocument = async (
    userID,
    email,
    company,
    name,
    tempStripeUserData
  ) => {
    const timestampSignup = Date.now(); // Get the current timestamp
  
    const userRef = doc(db, 'user', userID);
  
    await setDoc(userRef, {
      userID: userID,
      email: email,
      company: company,
      name: name,
      isDigestPaid: true,
      timestampSignup: timestampSignup,
      timestampLastUser: timestampSignup,
      priceId: tempStripeUserData.priceId,
      stripeCustomerId: tempStripeUserData.stripeCustomerId,
      subscriptionStatus: tempStripeUserData.subscriptionStatus,
      subscriptionId: tempStripeUserData.subscriptionId,
      subscriptionCreated: tempStripeUserData.subscriptionCreated,
      subscriptionProduct: tempStripeUserData.subscriptionProduct,
      subscriptionCurrentPeriodEnd: tempStripeUserData.subscriptionCurrentPeriodEnd,
    });
  
    return true; // Return true if document creation was successful
  };


  



// This function will call the getSolDashSearchResults cloud function
export const sendSupportEmailToAdmin = async (email, message) => {
  try {
    // Prepare the callable cloud function using httpsCallable
    const sendAppSupportEmailToAdmin = httpsCallable(functions, 'sendAppSupportEmailToAdmin');

    const data = {
        email: email,
        message: message
    };

    // Call the cloud function with the prepared data
    const result = await sendAppSupportEmailToAdmin(data);

    return result.data;
  } catch (error) {
    console.error('Error fetching search results:', error);
    throw error;  // Optionally throw the error for further handling
  }
};




// Function to get subscription details based on the user's email
export const getSubscriptionDetails = async (userID) => {
    try {
      const getSubscriptionDetailsFunc = httpsCallable(functions, 'getSubscriptionDetails');
      const response = await getSubscriptionDetailsFunc({ userID }); // Pass email as parameter
      return response.data; // Return the data from the cloud function
    } catch (error) {
      console.error('Error getting subscription details:', error);
      throw new Error('Failed to retrieve subscription details.');
    }
  };




  
// Function to change the user's subscription by userID and newPriceId
export const changeSubscription = async (userID, newPriceId) => {
    try {
      const changeSubscriptionFunc = httpsCallable(functions, 'changeSubscription');
      const response = await changeSubscriptionFunc({ userID, newPriceId }); // Pass userID and newPriceId as parameters
      return response.data; // Return the data from the cloud function
    } catch (error) {
      console.error('Error changing subscription:', error);
      throw new Error('Failed to change subscription.');
    }
  };

// Change a user's email address
// Re-authenticates, sends verification to new email, then updates Firestore collections
export const changeUserEmail = async (currentPassword, newEmail) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found.');
  }

  const oldEmail = user.email;
  const normalizedNewEmail = newEmail.trim().toLowerCase();

  // STEP 1: Re-authenticate the user
  const credential = EmailAuthProvider.credential(oldEmail, currentPassword);
  await reauthenticateWithCredential(user, credential);

  // STEP 2: Send verification to the new email FIRST
  // If this fails (e.g. email already in use), no Firestore changes are made
  // The Auth email only changes after the user clicks the verification link
  await verifyBeforeUpdateEmail(user, normalizedNewEmail);

  // STEP 3: Update Firestore collections (only reached if verification email was sent)

  // 3a: Update 'user' collection — find documents where email matches old email
  const userQuery = query(collection(db, 'user'), where('email', '==', oldEmail));
  const userSnapshot = await getDocs(userQuery);
  for (const userDoc of userSnapshot.docs) {
    await updateDoc(userDoc.ref, { email: normalizedNewEmail });
  }

  // 3b: Update 'iap' collection — find documents where email matches old email
  const iapQuery = query(collection(db, 'iap'), where('email', '==', oldEmail));
  const iapSnapshot = await getDocs(iapQuery);
  for (const iapDoc of iapSnapshot.docs) {
    await updateDoc(iapDoc.ref, { email: normalizedNewEmail });
  }

  // 3c: Update 'otherEligibleUserEmails' collection
  // Check 'backdoor' and 'backdoorPlatform' documents for old email in 'emails' array
  const eligibleDocIds = ['backdoor', 'backdoorPlatform'];
  for (const docId of eligibleDocIds) {
    const eligibleRef = doc(db, 'otherEligibleUserEmails', docId);
    const eligibleSnap = await getDoc(eligibleRef);
    if (eligibleSnap.exists()) {
      const data = eligibleSnap.data();
      if (data.emails && Array.isArray(data.emails) && data.emails.includes(oldEmail)) {
        await updateDoc(eligibleRef, {
          emails: arrayRemove(oldEmail),
        });
        await updateDoc(eligibleRef, {
          emails: arrayUnion(normalizedNewEmail),
        });
      }
    }
  }

  return "Verification email sent to your new address. Please check your inbox to complete the change.";
};

// Delete a user's account (Firestore document and authentication record)
export const deleteAccount = async (user) => {
    if (!user) {
      throw new Error('No user provided for account deletion.');
    }

    try {
      // Remove the user document from Firestore
      await deleteDoc(doc(db, 'user', user.uid));
    } catch (error) {
      console.error('Error deleting user document:', error);
      // Continue to attempt deletion from auth even if Firestore removal fails
    }

    try {
      await deleteUser(user);
    } catch (error) {
      console.error('Error deleting authentication user:', error);
      throw error;
    }
    return true;
};
